import "./styles.scss";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ImageLightbox from "components/ImageLightbox";
import classnames from "classnames";
import moment from "moment";

import TextEditor, {
  convertToHTML,
  htmlToDraftBlocks,
  getPlainText,
} from "components/TextEditor";
import { ModalEdit } from "./components";

import { stripExtension, isImage, getExtension } from "./helpers";

import useResponsive from "libs/helpers/useResponsive";
import withProtocol from "libs/helpers/withProtocol";

import PlaceholderAvatarImage from "assets/images/avatar-placeholder.png";
import { ReactComponent as EditIcon } from "assets/icons/edit.svg";

const mainClass = "task-view-message";

const UserMessage = ({
  id,
  date,
  edited_date,
  user,
  message,
  attachments = [],
  me,
  file_progress,
  editMessage,
  isMessageSending,
  isCompletelyDisabled,
  onEditModeChange,
  isFileDropTarget,
  droppedFiles,
  onFilesDropHandled,
  isLast,
  isInViewport,
}) => {
  const { t } = useTranslation();
  const { max } = useResponsive();

  const [content, setContent] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesToRemove, setFilesToRemove] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!editMode) return undefined;

    onEditModeChange(id, true);
    return () => onEditModeChange(id, false);
  }, [editMode, id, onEditModeChange]);

  useEffect(() => {
    if (message !== "") {
      setContent(htmlToDraftBlocks(message));
    }
  }, [message]);

  useEffect(() => {
    if (attachments?.length > 0 && !editMode) {
      setFiles(attachments);
    }
  }, [attachments, editMode]);

  useEffect(() => {
    if (max.lg) return;

    if (isLast && editMode) {
      document.querySelector("#chat-messages").scrollIntoView({
        block: "end",
        inline: "nearest",
      });
    }
  }, [isLast, editMode, max.lg]);

  const html = convertToHTML(content);
  const plainText = getPlainText(html);
  const isEmpty = plainText === "";

  const handleSubmit = async () => {
    if (typeof editMessage === "function")
      await editMessage(
        id,
        html ?? "",
        files.filter((file) => !file?.key),
        filesToRemove,
        true,
      );

    setEditMode(false);
  };

  const isUploading = file_progress !== undefined && file_progress !== null;

  const handleEditActivate = () => {
    if (editMode) onEditModeChange(id, true);
  };

  return (
    <>
      <div
        className={classnames(mainClass, {
          [`${mainClass}--editing`]: !max.sm && editMode,
          [`${mainClass}--in-viewport`]: isInViewport,
          [`${mainClass}--file-drop-target`]:
            !max.sm && editMode && isFileDropTarget,
        })}
        onClickCapture={handleEditActivate}
        onFocusCapture={handleEditActivate}
      >
        <div className={`${mainClass}__header`}>
          <div className={`${mainClass}__header-avatar`}>
            <img src={user?.avatar || PlaceholderAvatarImage} alt="" />
          </div>
          <p>
            {moment(date)?.format("lll")} - {t("Comment added by")}{" "}
            {user?.name ?? t("Unknown")}.
          </p>
          {!!me && (
            <button
              type="button"
              disabled={isUploading || isMessageSending || isCompletelyDisabled}
              onClick={() =>
                setEditMode((prevEdit) => {
                  if (prevEdit) setContent(htmlToDraftBlocks(message));

                  return !prevEdit;
                })
              }
            >
              <EditIcon />{" "}
              <span>
                {t(!max.sm && editMode ? "Cancel edit" : "Edit comment")}
              </span>
            </button>
          )}
        </div>
        {max.sm || !editMode ? (
          <>
            {message !== "" && (
              <div className={`${mainClass}__content`}>
                <div dangerouslySetInnerHTML={{ __html: message }} />
              </div>
            )}
            {attachments?.length > 0 && (
              <div className={`${mainClass}__attachments`}>
                {attachments.map(({ _id, name, location }, index) => {
                  const url = withProtocol(location);
                  const isImageAttachment = isImage(name) || isImage(url);
                  const AttachmentTag = isImageAttachment ? "button" : "a";

                  return (
                    <AttachmentTag
                      key={_id ?? `${name}-${index}`}
                      className={`${mainClass}__attachment`}
                      type={isImageAttachment ? "button" : undefined}
                      href={isImageAttachment ? undefined : url}
                      target={isImageAttachment ? undefined : "_blank"}
                      rel={isImageAttachment ? undefined : "noopener noreferrer"}
                      onClick={
                        isImageAttachment ? () => setLightbox(url) : undefined
                      }
                    >
                      <span>
                        {isImageAttachment ? (
                          <img src={url} alt="" />
                        ) : (
                          <span>{getExtension(name)}</span>
                        )}
                      </span>
                      <small>
                        <span>{stripExtension(name)}</span>
                      </small>
                    </AttachmentTag>
                  );
                })}
              </div>
            )}
            {edited_date && (
              <p className={`${mainClass}__edited`}>
                {t("Last edit")}: {moment(edited_date).format("lll")}
              </p>
            )}
          </>
        ) : (
          <div className={`${mainClass}__editor`}>
            <TextEditor
              id={`message-${id}`}
              content={content}
              setContent={setContent}
              files={files}
              setFiles={setFiles}
              droppedFiles={droppedFiles}
              onFilesDropHandled={onFilesDropHandled}
              autoFocus
              isInline
              isDisabled={isUploading || isCompletelyDisabled}
              isMessageSending={isMessageSending}
              isSubmitDisabled={isEmpty && !(files.length > 0)}
              onPreviewClick={(url) => setLightbox(url)}
              onDeleteClick={(file) =>
                file?.key
                  ? setFilesToRemove((prevFiles) => [...prevFiles, file.key])
                  : undefined
              }
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      {max.sm && (
        <ModalEdit
          id={`message-${id}`}
          content={content}
          setContent={setContent}
          files={files}
          setFiles={setFiles}
          droppedFiles={droppedFiles}
          onFilesDropHandled={onFilesDropHandled}
          isOpen={editMode}
          isDisabled={isUploading || isCompletelyDisabled}
          isMessageSending={isMessageSending}
          isSubmitDisabled={isEmpty && !(files.length > 0)}
          onPreviewClick={(url) => setLightbox(url)}
          onDeleteClick={(file) =>
            file?.key
              ? setFilesToRemove((prevFiles) => [...prevFiles, file.key])
              : undefined
          }
          onSubmit={handleSubmit}
          onClose={() => setEditMode(false)}
          onActivate={handleEditActivate}
          isFileDropTarget={isFileDropTarget}
        />
      )}

      {!!lightbox && (
        <ImageLightbox
          src={lightbox}
          onClose={() => setLightbox(null)}
          toolbarButtons={[
            <a
              key="download"
              className={`${mainClass}__lightbox-download`}
              href={lightbox}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("Download")}
            </a>,
          ]}
        />
      )}
    </>
  );
};

export default UserMessage;
